import { useAccount, useSwitchChain, useChainId } from 'wagmi';
import { supportedChains } from '../config/chains';
import { useState } from 'react';

export function ChainSwitcher() {
  const { chainId } = useAccount();
  const { switchChain, isPending, error } = useSwitchChain();
  const currentChainId = useChainId();
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const getChainName = (id: number) => {
    const chain = supportedChains.find(c => c.id === id);
    return chain?.name || `Chain ${id}`;
  };

  const getChainLogo = (id: number): string | null => {
    // Use CoinGecko or popular CDN for chain logos
    if (id === 1) {
      // Ethereum logo
      return 'https://assets.coingecko.com/coins/images/279/large/ethereum.png';
    }
    if (id === 42161) {
      // Arbitrum logo
      return 'https://assets.coingecko.com/coins/images/16547/large/photo_2023-03-29_21.47.00.jpeg';
    }
    if (id === 33139) {
      // ApeChain logo - Updated from Figma
      return '/apechain-logo.png';
    }
    return null;
  };

  const getChainEmoji = (id: number): string => {
    if (id === 1) return '🔷';
    if (id === 42161) return '🔵';
    if (id === 33139) return '🦍';
    return '⛓️';
  };

  const handleImageError = (id: number) => {
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 rounded-lg border" 
         style={{ backgroundColor: '#1A1D29', borderColor: '#2D2F36', borderRadius: 'var(--radius-sm)' }}>
      <span className="text-xs uppercase tracking-wide" style={{ fontWeight: 500, color: 'rgba(229, 229, 229, 0.7)' }}>
        Network:
      </span>
      <div className="flex items-center gap-2">
        {supportedChains.map((chain) => {
          const isActive = chain.id === currentChainId;
          return (
            <button
              key={chain.id}
              onClick={() => switchChain({ chainId: chain.id })}
              disabled={isActive || isPending}
              className={`px-3 py-1.5 rounded-lg border transition-all text-xs font-semibold flex items-center gap-2 ${
                isActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                backgroundColor: isActive ? '#2081E2' : '#0D1117',
                borderColor: isActive ? '#2081E2' : '#2D2F36',
                color: isActive ? '#FFFFFF' : '#E5E5E5',
                cursor: isActive || isPending ? 'not-allowed' : 'pointer',
                borderRadius: 'var(--radius-sm)',
                fontWeight: 600
              }}
            >
              {getChainLogo(chain.id) && !imageErrors[chain.id] ? (
                <img 
                  src={getChainLogo(chain.id)!} 
                  alt={chain.name}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0
                  }}
                  onError={() => handleImageError(chain.id)}
                />
              ) : (
                <span style={{ fontSize: '16px', lineHeight: 1 }}>{getChainEmoji(chain.id)}</span>
              )}
              <span>{chain.name}</span>
            </button>
          );
        })}
      </div>
      {isPending && (
        <span className="text-xs" style={{ color: 'rgba(229, 229, 229, 0.7)' }}>Switching...</span>
      )}
      {error && (
        <span className="text-xs" style={{ color: '#EF4444' }}>Error: {error.message}</span>
      )}
    </div>
  );
}
